import React from "react";
import DOMPurify from "dompurify";

const ACTION_META = {
  user_asked: { label: "User Asked", shortCode: "U", tone: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  classify_user_intent: { label: "Intent Classification", shortCode: "I", tone: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  search_knowledge_base: { label: "Knowledge Base Search", shortCode: "KB", tone: "#0f766e", bg: "#ecfeff", border: "#99f6e4" },
  google_search: { label: "Web Search", shortCode: "WS", tone: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  evaluate_search_results: { label: "Result Evaluation", shortCode: "EV", tone: "#a16207", bg: "#fefce8", border: "#fde68a" },
  refine_search_query: { label: "Query Refinement", shortCode: "RQ", tone: "#9333ea", bg: "#faf5ff", border: "#e9d5ff" },
  ask_user_for_clarification: { label: "Clarification", shortCode: "CL", tone: "#b45309", bg: "#fffbeb", border: "#fcd34d" },
  get_product_knowledge: { label: "Product Lookup", shortCode: "PK", tone: "#be123c", bg: "#fff1f2", border: "#fecdd3" },
  url_context: { label: "URL Context", shortCode: "URL", tone: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
  followup_requested: { label: "Follow-up Requested", shortCode: "FR", tone: "#047857", bg: "#ecfdf5", border: "#a7f3d0" },
  followup_sent: { label: "Follow-up Sent", shortCode: "FS", tone: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  final_response: { label: "Final Response", shortCode: "R", tone: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
};

const KNOWN_RESULT_KEYS = {
  assessment: true,
  candidates: true,
  citations: true,
  confidence: true,
  confidence_score: true,
  context: true,
  documents: true,
  error: true,
  intent: true,
  intent_code: true,
  items: true,
  message: true,
  name: true,
  page_type_priority: true,
  product_slugs: true,
  query: true,
  reason: true,
  reason_for_refinement: true,
  reasoning: true,
  refined_query: true,
  refusal_reason: true,
  resolved_tool: true,
  response: true,
  response_kind: true,
  response_mode: true,
  response_type: true,
  text: true,
  tool_name: true,
  wasSuccessful: true,
  web_search_queries: true,
};

const containerStyle = {
  background: "#ffffff",
  borderRadius: "16px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
};

const headerStyle = {
  padding: "18px 20px",
  borderBottom: "1px solid #eef2f7",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
};

const summaryWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "12px",
};

const summaryChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 10px",
  borderRadius: "999px",
  border: "1px solid #dbe3ee",
  background: "#ffffff",
  color: "#475467",
  fontSize: "12px",
  fontWeight: 600,
};

const timelineWrapStyle = {
  position: "relative",
  padding: "20px 18px 8px 18px",
};

const lineStyle = {
  position: "absolute",
  left: "37px",
  top: "24px",
  bottom: "24px",
  width: "2px",
  background: "linear-gradient(180deg, #dbeafe 0%, #e5e7eb 100%)",
};

const cardStyle = {
  flex: 1,
  minWidth: 0,
  background: "#ffffff",
  border: "1px solid #e6ecf3",
  borderRadius: "14px",
  padding: "14px 16px",
  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
};

const sectionTitleStyle = {
  margin: "0 0 8px 0",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#667085",
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#667085",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const bodyTextStyle = {
  color: "#1f2937",
  fontSize: "14px",
  lineHeight: 1.6,
};

const codeBlockStyle = {
  margin: 0,
  padding: "12px",
  borderRadius: "10px",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
  fontSize: "12px",
  lineHeight: 1.55,
  overflowX: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const htmlResponseStyle = {
  padding: "12px 14px",
  borderRadius: "12px",
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  color: "#1f2937",
  fontSize: "14px",
  lineHeight: 1.6,
  wordBreak: "break-word",
};

const chipBaseStyle = {
  display: "inline-flex",
  alignItems: "center",
  maxWidth: "100%",
  padding: "5px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 600,
  border: "1px solid #dbe3ee",
  background: "#f8fafc",
  color: "#344054",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: "10px",
};

const listStyle = {
  margin: 0,
  paddingLeft: "18px",
  color: "#334155",
};

const getActionMeta = (action) => {
  if (action && ACTION_META[action]) {
    return ACTION_META[action];
  }

  return {
    label: formatActionName(action),
    shortCode: action ? action.slice(0, 3).toUpperCase() : "?",
    tone: "#475467",
    bg: "#f8fafc",
    border: "#d0d5dd",
  };
};

function formatActionName(action) {
  if (!action) {
    return "Unknown Action";
  }

  return action
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function toArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function isScalar(value) {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function formatScalar(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }

  return value || "";
}

function formatConfidence(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }

  if (value <= 1) {
    return `${Math.round(value * 100)}%`;
  }

  return `${Math.round(value)}%`;
}

function getGroundingMetadata(result) {
  if (
    result &&
    result.candidates &&
    result.candidates.length > 0 &&
    result.candidates[0] &&
    result.candidates[0].groundingMetadata
  ) {
    return result.candidates[0].groundingMetadata;
  }

  if (result && result.groundingMetadata) {
    return result.groundingMetadata;
  }

  return null;
}

function getDocumentItems(result) {
  const docs = toArray(result && result.documents).map((item) => ({
    title: item && item.document_title ? item.document_title : "Untitled document",
    url: item && item.document_url ? item.document_url : "",
    metadata: item && item.metadata ? item.metadata : null,
  }));

  const items = toArray(result && result.items).map((item) => ({
    title: item && item.page_title ? item.page_title : "Untitled document",
    url: item && item.source_url ? item.source_url : "",
    metadata: item && item.metadata ? item.metadata : null,
  }));

  const citations = toArray(result && result.citations).map((item) => ({
    title: item && item.page_title ? item.page_title : "Untitled citation",
    url: item && item.source_url ? item.source_url : "",
    metadata: item && item.metadata ? item.metadata : null,
  }));

  return docs.concat(items, citations);
}

function getTaxonomyPath(item) {
  if (
    item &&
    item.metadata &&
    item.metadata.hierarchy &&
    Array.isArray(item.metadata.hierarchy.taxonomy_path)
  ) {
    return item.metadata.hierarchy.taxonomy_path.filter(Boolean);
  }

  return [];
}

function getGroundingLinks(result) {
  const metadata = getGroundingMetadata(result);

  if (!metadata || !Array.isArray(metadata.groundingChunks)) {
    return [];
  }

  return metadata.groundingChunks
    .map((chunk) => {
      if (!chunk || !chunk.web) {
        return null;
      }

      return {
        title: chunk.web.title || chunk.web.uri || "External link",
        url: chunk.web.uri || "",
      };
    })
    .filter(Boolean);
}

function getSearchQueries(step) {
  const result = step && step.result ? step.result : null;
  const metadata = getGroundingMetadata(result);
  const queries = [];

  if (result && Array.isArray(result.web_search_queries)) {
    queries.push.apply(queries, result.web_search_queries);
  }

  if (metadata && Array.isArray(metadata.webSearchQueries)) {
    queries.push.apply(queries, metadata.webSearchQueries);
  }

  if (result && typeof result.query === "string" && result.query.trim()) {
    queries.unshift(result.query.trim());
  } else if (step && typeof step.message === "string" && step.message.trim()) {
    if (
      step.action === "search_knowledge_base" ||
      step.action === "google_search" ||
      step.action === "refine_search_query" ||
      step.action === "url_context"
    ) {
      queries.unshift(step.message.trim());
    }
  }

  return queries.filter(Boolean).filter((value, index, arr) => arr.indexOf(value) === index);
}

function getResponseText(step) {
  const result = step && step.result ? step.result : null;

  if (result && typeof result.text === "string" && result.text.trim()) {
    return result.text.trim();
  }

  if (result && typeof result.response === "string" && result.response.trim()) {
    return result.response.trim();
  }

  if (step && typeof step.message === "string" && step.message.trim()) {
    return step.message.trim();
  }

  return "";
}

function getAdditionalFields(result) {
  if (!result || typeof result !== "object") {
    return [];
  }

  return Object.keys(result)
    .filter((key) => !KNOWN_RESULT_KEYS[key])
    .map((key) => ({
      key,
      value: result[key],
    }))
    .filter((entry) => {
      if (entry.value === null || entry.value === undefined) {
        return false;
      }

      if (typeof entry.value === "string") {
        return entry.value.trim().length > 0;
      }

      if (Array.isArray(entry.value)) {
        return entry.value.length > 0;
      }

      if (typeof entry.value === "object") {
        return Object.keys(entry.value).length > 0;
      }

      return true;
    });
}

function sanitizeHtmlResponse(text) {
  if (!text || typeof text !== "string") {
    return "";
  }

  return DOMPurify.sanitize(text, {
    ALLOWED_URI_REGEXP: /^https?:\/\//i,
  });
}

const Section = ({ title, children }) => {
  if (!children) {
    return null;
  }

  return (
    <div style={{ marginTop: "14px" }}>
      <div style={sectionTitleStyle}>{title}</div>
      {children}
    </div>
  );
};

const Chip = ({ children, tone, background, border }) => (
  <span
    style={{
      ...chipBaseStyle,
      color: tone || chipBaseStyle.color,
      background: background || chipBaseStyle.background,
      borderColor: border || chipBaseStyle.border,
    }}
  >
    {children}
  </span>
);

const TextBlock = ({ children, accent }) => {
  if (!children) {
    return null;
  }

  return (
    <div
      style={{
        ...bodyTextStyle,
        padding: "12px 14px",
        borderRadius: "12px",
        background: accent ? `${accent}10` : "#f8fafc",
        border: `1px solid ${accent ? `${accent}33` : "#e2e8f0"}`,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {children}
    </div>
  );
};

const LinkList = ({ items }) => {
  const safeItems = toArray(items);

  if (safeItems.length === 0) {
    return null;
  }

  return (
    <ol style={{ ...listStyle, marginBottom: 0 }}>
      {safeItems.map((item, index) => {
        const taxonomy = getTaxonomyPath(item);
        const title = item.title || item.url || `Link ${index + 1}`;

        return (
          <li key={`${title}-${index}`} style={{ marginBottom: "10px" }}>
            <a
              href={item.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#0f62fe",
                textDecoration: "none",
                fontWeight: 600,
                wordBreak: "break-word",
              }}
            >
              {title}
            </a>
            {taxonomy.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
                {taxonomy.map((value, taxonomyIndex) => (
                  <Chip key={`${value}-${taxonomyIndex}`}>{value}</Chip>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const AdditionalField = ({ label, value }) => {
  if (isScalar(value)) {
    return (
      <div>
        <div style={labelStyle}>{formatActionName(label)}</div>
        <div style={{ ...bodyTextStyle, marginTop: "4px" }}>{formatScalar(value)}</div>
      </div>
    );
  }

  if (Array.isArray(value) && value.every(isScalar)) {
    return (
      <div>
        <div style={labelStyle}>{formatActionName(label)}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "8px" }}>
          {value.map((item, index) => (
            <Chip key={`${label}-${index}`}>{formatScalar(item)}</Chip>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={labelStyle}>{formatActionName(label)}</div>
      <pre style={{ ...codeBlockStyle, marginTop: "8px" }}>
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
};

const StepDetails = ({ step, meta }) => {
  const result = step && step.result ? step.result : {};
  const isFinalResponse = step && step.action === "final_response";
  const documents = getDocumentItems(result);
  const groundingLinks = getGroundingLinks(result);
  const queries = getSearchQueries(step);
  const responseText = getResponseText(step);
  const sanitizedResponseHtml = isFinalResponse ? sanitizeHtmlResponse(responseText) : "";
  const confidence =
    typeof result.confidence === "number" ? result.confidence : result.confidence_score;
  const additionalFields = getAdditionalFields(result);
  const scalarMeta = [];

  if (result && (result.intent || result.intent_code)) {
    scalarMeta.push({ label: "Intent", value: result.intent || result.intent_code });
  }

  if (confidence !== null && confidence !== undefined) {
    scalarMeta.push({ label: "Confidence", value: formatConfidence(confidence) || formatScalar(confidence) });
  }

  if (result && (result.resolved_tool || result.tool_name)) {
    scalarMeta.push({ label: "Tool", value: result.resolved_tool || result.tool_name });
  }

  if (result && result.response_kind) {
    scalarMeta.push({ label: "Response Kind", value: result.response_kind });
  }

  if (result && result.response_type) {
    scalarMeta.push({ label: "Response Type", value: result.response_type });
  }

  if (result && result.response_mode) {
    scalarMeta.push({ label: "Response Mode", value: result.response_mode });
  }

  if (result && result.wasSuccessful !== undefined) {
    scalarMeta.push({ label: "Successful", value: result.wasSuccessful ? "Yes" : "No" });
  }

  return (
    <div>
      {step && step.action === "user_asked" && responseText && (
        <TextBlock accent={meta.tone}>{responseText}</TextBlock>
      )}

      {!isFinalResponse && step && step.action !== "user_asked" && step.message && step.message.trim() && (
        <Section title="Input">
          <TextBlock>{step.message}</TextBlock>
        </Section>
      )}

      {!isFinalResponse && scalarMeta.length > 0 && (
        <Section title="Summary">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {scalarMeta.map((item, index) => (
              <Chip
                key={`${item.label}-${index}`}
                tone={meta.tone}
                background={meta.bg}
                border={meta.border}
              >
                {item.label}: {item.value}
              </Chip>
            ))}
          </div>
        </Section>
      )}

      {result && result.assessment && (
        <Section title="Assessment">
          <TextBlock>{result.assessment}</TextBlock>
        </Section>
      )}

      {result && result.reasoning && (
        <Section title="Reasoning">
          <TextBlock>{result.reasoning}</TextBlock>
        </Section>
      )}

      {result && result.reason_for_refinement && (
        <Section title="Refinement Reason">
          <TextBlock>{result.reason_for_refinement}</TextBlock>
        </Section>
      )}

      {result && result.refined_query && (
        <Section title="Refined Query">
          <TextBlock accent={meta.tone}>{result.refined_query}</TextBlock>
        </Section>
      )}

      {result && result.reason && (
        <Section title="Reason">
          <TextBlock>{result.reason}</TextBlock>
        </Section>
      )}

      {result && result.refusal_reason && (
        <Section title="Refusal Reason">
          <TextBlock>{result.refusal_reason}</TextBlock>
        </Section>
      )}

      {!isFinalResponse && queries.length > 0 && (
        <Section title={step.action === "google_search" ? "Search Queries" : "Queries"}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {queries.map((query, index) => (
              <Chip key={`${query}-${index}`}>{query}</Chip>
            ))}
          </div>
        </Section>
      )}

      {result && Array.isArray(result.product_slugs) && result.product_slugs.length > 0 && (
        <Section title="Products">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {result.product_slugs.map((product, index) => (
              <Chip key={`${product}-${index}`}>{product}</Chip>
            ))}
          </div>
        </Section>
      )}

      {result && Array.isArray(result.page_type_priority) && result.page_type_priority.length > 0 && (
        <Section title="Page Types">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {result.page_type_priority.map((pageType, index) => (
              <Chip key={`${pageType}-${index}`}>{pageType}</Chip>
            ))}
          </div>
        </Section>
      )}

      {result && result.name && (
        <Section title="Product Recommendation">
          <TextBlock accent={meta.tone}>{result.name}</TextBlock>
        </Section>
      )}

      {result && result.context && (
        <Section title="Context">
          <TextBlock>{result.context}</TextBlock>
        </Section>
      )}

      {!isFinalResponse && documents.length > 0 && (
        <Section title="Documents">
          <LinkList items={documents} />
        </Section>
      )}

      {!isFinalResponse && groundingLinks.length > 0 && (
        <Section title="Searched Links">
          <LinkList items={groundingLinks} />
        </Section>
      )}

      {isFinalResponse && sanitizedResponseHtml && (
        <div style={{ marginTop: "14px" }}>
          <div
            style={htmlResponseStyle}
            dangerouslySetInnerHTML={{ __html: sanitizedResponseHtml }}
          />
        </div>
      )}

      {result && result.error && (
        <Section title="Error">
          <TextBlock>{result.error}</TextBlock>
        </Section>
      )}

      {additionalFields.length > 0 && (
        <Section title="Additional Data">
          <div style={gridStyle}>
            {additionalFields.map((field) => (
              <AdditionalField key={field.key} label={field.key} value={field.value} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

export const ThinkingProcessTimeline = ({ data }) => {
  const steps = Array.isArray(data) ? data : [];
  const documentCount = steps.reduce((count, step) => count + getDocumentItems(step && step.result).length, 0);
  const queryCount = steps.reduce((count, step) => count + getSearchQueries(step).length, 0);
  const finalResponseCount = steps.filter((step) => step && step.action === "final_response").length;

  if (steps.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: "24px 20px", color: "#667085" }}>No interaction trace available.</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start" }}>
          <div>
            <h5 style={{ margin: 0, fontWeight: 700, color: "#101828" }}>Thinking Process</h5>
            <p style={{ margin: "6px 0 0 0", color: "#667085", fontSize: "13px", lineHeight: 1.5 }}>
              Detailed interaction trace for how the response was composed.
            </p>
          </div>
          <div style={{ ...summaryChipStyle, whiteSpace: "nowrap" }}>{steps.length} steps</div>
        </div>
        <div style={summaryWrapStyle}>
          <span style={summaryChipStyle}>Queries: {queryCount}</span>
          <span style={summaryChipStyle}>Documents: {documentCount}</span>
          <span style={summaryChipStyle}>Responses: {finalResponseCount}</span>
        </div>
      </div>

      <div style={timelineWrapStyle}>
        <div style={lineStyle} />
        {steps.map((step, index) => {
          const meta = getActionMeta(step && step.action);
          const stepNumber = step && (step.step || step.step === 0) ? step.step : index + 1;
          const createdAt =
            step && step.created_at
              ? new Date(step.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
              : "";

          return (
            <div
              key={`${step && step.action ? step.action : "step"}-${index}`}
              style={{ display: "flex", alignItems: "flex-start", gap: "14px", marginBottom: "18px", position: "relative" }}
            >
              <div
                style={{
                  width: "40px",
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "14px",
                    border: `1px solid ${meta.border}`,
                    background: meta.bg,
                    color: meta.tone,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: meta.shortCode.length > 2 ? "11px" : "13px",
                    letterSpacing: "0.02em",
                    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
                  }}
                  title={meta.label}
                >
                  {meta.shortCode}
                </div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#98a2b3" }}>{stepNumber}</div>
              </div>

              <div style={cardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{meta.label}</div>
                    <div style={{ marginTop: "4px" }}>
                      <Chip tone={meta.tone} background={meta.bg} border={meta.border}>
                        {step && step.type ? step.type : "system"}
                      </Chip>
                    </div>
                  </div>
                  {createdAt && (
                    <div style={{ color: "#98a2b3", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>
                      {createdAt}
                    </div>
                  )}
                </div>

                <div style={{ marginTop: "12px" }}>
                  <StepDetails step={step || {}} meta={meta} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/ai/ThinkingProcessTimeline.js